import {
  BadgeData,
  BadgeProps,
  BadgesSectionProps,
} from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import badgeEmpty from "assets/svg/badge-empty-state.svg";
import { useState } from "react";
import SideDrawerWrapper from "./SideDrawerWrapper";
import closeIcon from "assets/svg/close-icon.svg";
import { ArrowRightIcon } from "assets/icons";
import { useGetEvents } from "hooks/query/useEvents";
import { EventListItem } from "./PastEvents";
import eventImagePlaceholder from "assets/image/user-event-placeholder.jpg";
import Loader from "components/UI/Loader";
import { Link } from "react-router-dom";
import { getAppUrl } from "utils/subdomainDetection";

// Badges Component
export default function BadgesSection({
  badges,
  userName,
  badgeMap,
  speakerData,
}: BadgesSectionProps) {
  const [seeAllBadges, setSeeAllBadges] = useState(false);
  const [openBadgeDetailDrawer, setOpenBadgeDetailDrawer] = useState(false);
  const [agendaType, setAgendaType] = useState<string | undefined>(undefined);
  const [activeBadge, setActiveBadge] = useState<BadgeData | null>(null);

  const { data: Badgeevents, isLoading } = useGetEvents({
    speakerId: userName,
    agendaType: activeBadge?.label,
    startDate: "2023-05-22",
    pageLimit: 100,
    extendSpeakerSummary: true,
  });
  // Ensure we always have 4 badges for the main display
  const filledBadges = [...badges];
  while (filledBadges.length < 4) {
    filledBadges.push({
      color: "",
      label: "",
      badge: badgeEmpty,
      count: 0,
    });
  }

  // Ensure we always have 9 badges for the drawer display
  const drawerBadges = [...badges];
  while (drawerBadges.length < 15) {
    drawerBadges.push({
      color: "",
      label: "",
      badge: badgeEmpty,
      count: 0,
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg sm:text-xl font-monaMedium text-[#333333]">
          Badges
        </h2>
        {badges.length > 4 && (
          <button
            // onClick={handleBookingClick}
            className="text-[#344054] border border-[#D0D5DD] rounded-md flex items-center gap-x-2 px-1.5 py-1 font-sfMedium hover:bg-[#F2ECE2]"
            onClick={() => setSeeAllBadges(true)}
          >
            All badges
            <ArrowRightIcon />
          </button>
        )}
      </div>
      <div className="bg-[#F2ECE2] rounded-[20px] grid grid-cols-2 gap-4 pt-10 px-4 h-full pb-5 cursor-pointer hover:shadow-lg transition-shadow duration-300">
        {filledBadges.slice(0, 4).map((badge, index) => (
          <Badge
            key={index}
            {...badge}
            setActiveBadge={setActiveBadge}
            setOpenBadgeDetailDrawer={setOpenBadgeDetailDrawer}
          />
        ))}
      </div>

      <SideDrawerWrapper
        openDrawer={seeAllBadges}
        handleCloseDrawer={() => setSeeAllBadges(false)}
      >
        <section className="h-full flex flex-col bg-[#FDF6EE]">
          {/* Header */}
          <div className="flex items-center justify-between py-3 px-5 border-b border-[#D5DBE1] bg-[#F2ECE2]">
            <h3 className="text-[20px] font-monaSemiBold text-[#30313D]">
              All Badges
            </h3>
            <button
              onClick={() => setSeeAllBadges(false)}
              className="cursor-pointer"
            >
              <img src={closeIcon} alt="close" />
            </button>
          </div>

          {/* Badges Grid */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="grid grid-cols-3 gap-4">
              {drawerBadges.map((badge, index) => (
                <Badge
                  key={index}
                  {...badge}
                  setActiveBadge={setActiveBadge}
                  setOpenBadgeDetailDrawer={setOpenBadgeDetailDrawer}
                />
              ))}
            </div>
          </div>
        </section>
      </SideDrawerWrapper>

      <SideDrawerWrapper
        openDrawer={openBadgeDetailDrawer}
        handleCloseDrawer={() => {
          // setSeeAllBadges(false);
          setOpenBadgeDetailDrawer(false);
        }}
      >
        <section className="h-full flex flex-col bg-[#FDF6EE]">
          {/* Header */}
          <div className="flex items-center justify-between py-3 px-5 border-b border-[#D5DBE1] bg-[#F2ECE2]">
            <h3 className="text-[20px] font-monaSemiBold text-[#30313D]">
              {activeBadge?.label} breakdown
            </h3>
            <button
              onClick={() => setOpenBadgeDetailDrawer(false)}
              className="cursor-pointer"
            >
              <img src={closeIcon} alt="close" />
            </button>
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <div>
              <div className="pt-5 w-full flex flex-col items-center px-5">
                <div className="flex flex-col items-center group cursor-pointer">
                  <img
                    src={
                      badgeMap[agendaType?.toLowerCase() || "keynote"] ||
                      badgeMap.keynote
                    }
                    alt="badge"
                    className="w-[100px] sm:w-[120px] group-hover:scale-110 transition-transform duration-300"
                  />
                  {activeBadge?.label && (
                    <div className="bg-[#292D32] text-white px-3 sm:px-2 py-1 rounded-[6px] text-xs sm:text-sm lg:text-lg font-sfMedium group-hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2">
                      {activeBadge?.label}

                      {activeBadge?.count > 1 && (
                        <span className="bg-[#43474D] px-1 py-0.5 text-xs md:text-base rounded-[4px]">
                          {" "}
                          x {activeBadge?.count}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <p className="font-sfMedium text-lg text-[#4B5563] mt-[11px]">
                  {speakerData.name
                    ? speakerData.name.split(" ")[0]
                      ? speakerData.name.split(" ")[0]
                      : speakerData.name
                    : ""}{" "}
                  has delivered{" "}
                  <span className="text-[#E3592E]">{activeBadge?.count}</span>{" "}
                  {activeBadge?.label} presentation
                </p>
              </div>

              <div className="p-5 grid grid-cols-1 gap-y-5">
                {Badgeevents &&
                  Badgeevents.data.events
                    .map((event) => ({
                      id: event.id,
                      title: event.title,
                      image:
                        event.banner ||
                        event.bannerUrl ||
                        eventImagePlaceholder,
                      date: event.timeStarted || event.startDate,
                      attendees: event.attendees || 0,
                      engagementScore: event.engagementScore || 0,
                      rating: event.rating || 0,
                      ratingCount: event.ratingCount || 0,
                    }))
                    .map((event_, index) => (
                      <Link to={getAppUrl(`/event-details/${event_.id}`)}>
                        <EventListItem key={index} {...event_} />
                      </Link>
                    ))}
              </div>
            </div>
          )}
        </section>
      </SideDrawerWrapper>
    </div>
  );
}

// Badge Component
function Badge({
  color,
  label,
  badge,
  count,
  setActiveBadge,
  setOpenBadgeDetailDrawer,
}: BadgeProps) {
  return (
    <div
      className="flex flex-col items-center group cursor-pointer"
      onClick={() => {
        if (!label) return;

        setActiveBadge?.({ color, label, badge, count });
        setOpenBadgeDetailDrawer?.(true);
      }}
    >
      <img
        src={badge}
        alt="badge"
        className="w-[100px] sm:w-[120px] group-hover:scale-110 transition-transform duration-300"
      />
      {label && (
        <div className="bg-gray-800 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium group-hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2">
          {label}

          {count > 1 && (
            <span className="bg-[#43474D] px-1 py-0.5 text-xs rounded-[4px]">
              {" "}
              x {count}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
